import { Intent } from "@blueprintjs/core";
import { DateToLocal } from "components/dates";
import { Filter, FilterButton } from "components/filtering";
import { FormLocationSelector, FormTextInput } from "components/form-fields";
import { ListingTable } from "components/listing";
import { NotificationToaster } from "components/notifications";
import { EmployeeStatus } from "components/status";
import { Tooltip } from "components/tooltip";
import { UserHasAccess } from "components/user-access";
import { useDebounce } from "hooks/useDebounce";
import { useQuery } from "hooks/useQuery";
import { noPerformReviewAccessMessage, noReviewTopicAccessMessage, pendingStatusBands } from "pages/employee-reviews/shared";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { EmployeeReviewsService, EmployeeService } from "services";
import { SetFiltering } from "state/actions/Actions";

const statusFilterItems = Object.entries(pendingStatusBands).map(([id, band]) => ({ id: id, name: band.label }));

// The listing only needs View, but the complete-review wizard gates on Perform, so a
// user without it cannot open any review from here.
const performAction = "EmployeeReview:Perform";

const tableHeaders = ["Employee", "Location", "Review Type", "Due Date", "Status", "Raised By", "Topics"];
const sortableHeaders = [
    { headerName: "Employee", sortName: "employee" },
    { headerName: "Location", sortName: "location" },
    { headerName: "Review Type", sortName: "reviewtype" },
    { headerName: "Due Date", sortName: "duedate" },
    { headerName: "Status", sortName: "status" }
];

// Pending reviews list soonest-due first by default.
const defaultSortHeader = sortableHeaders.find((header) => header.sortName === "duedate");

function renderStatusBand(statusBand) {
    const band = pendingStatusBands[statusBand];

    if (band == null) {
        return statusBand;
    }

    return <span key={`status-${statusBand}`} className={band.className}>{band.label}</span>;
}

export function EmployeeReviewsPending() {
    const defaultRecordSize = 25;
    const debounceTimeout = 750;
    const filterName = "employeeReviewsPending";

    const dispatch = useDispatch();
    const query = useQuery();
    const queryStatus = query.get("status");
    const canPerformReviews = UserHasAccess(performAction);

    const [initalSetupComplete, setInitalSetupComplete] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [pageFilters, setPageFilters] = useState([]);
    const [employeeStatusOptions, setEmployeeStatusOptions] = useState([]);
    const [employeeTypeOptions, setEmployeeTypeOptions] = useState([]);

    const [sortedDir, setSortedDir] = useState("A");
    const [sortedBy, setSortedBy] = useState(defaultSortHeader);

    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultRecordSize);
    const [tableRows, setTableRows] = useState([]);

    const [appliedFilters, setAppliedFilters] = useState([]);
    const [showFilters, setShowFilters] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const reduxLocations = useSelector((state) => state.employeeReviews.locations);
    const [useHierarchy, setUseHierarchy] = useState(true);
    const [useLocationDefaults, setUseLocationDefaults] = useState(null);
    const locationFilterOpen = useRef(false);

    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentPageNumber = useRef();
    const currentPageSize = useRef();
    const currentSearchTerm = useRef();
    const currentAppliedFilters = useRef();
    const currentLocations = useRef();

    const selectedFilters = useSelector((state) => state.filters[filterName]);

    const noDataMessage = "There are no pending reviews for the selected locations and filters.";

    if (!selectedFilters) {
        dispatch(SetFiltering(filterName, []));
    }

    function buildDrillThroughFilters() {
        if (queryStatus == null || queryStatus === "") {
            return [];
        }

        return queryStatus.split(",").map((statusValue) => ({
            name: "Status",
            key: "Status",
            value: statusValue,
            displayName: pendingStatusBands[statusValue]?.label ?? statusValue
        }));
    }

    useEffect(() => {
        Promise.all([
            EmployeeReviewsService.getOptions(),
            EmployeeService.getEmployeeOptions()
        ])
            .then(([options, employeeOptions]) => {
                setEmployeeStatusOptions(employeeOptions?.employeeStatuses ?? []);
                setEmployeeTypeOptions(employeeOptions?.employeeTypes ?? []);

                setPageFilters([
                    {
                        name: "Status",
                        displayName: "Status",
                        items: statusFilterItems
                    },
                    {
                        name: "ReviewType",
                        displayName: "Review Type",
                        items: (options?.reviewTypes ?? []).map((reviewType) => ({ id: reviewType.id.toString(), name: reviewType.name }))
                    },
                    {
                        name: "RaisedBy",
                        displayName: "Raised By",
                        items: (options?.raisedBy ?? []).map((raisedBy) => ({ id: raisedBy.id.toString(), name: raisedBy.name }))
                    }
                ]);

                // Apply the overview drill-through in the same render as initalSetupComplete,
                // so the first search already carries the seeded status filter. The dispatch
                // replaces the page's whole filter slice: a drill-down defines the entire
                // funnel state, discarding any previously persisted selections.
                const drillThroughFilters = buildDrillThroughFilters();
                if (drillThroughFilters.length > 0) {
                    setAppliedFilters(drillThroughFilters);
                    dispatch(SetFiltering(filterName, drillThroughFilters));
                }
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Failed to load review options. Filter choices may be incomplete.");
            })
            .finally(() => {
                setInitalSetupComplete(true);
            });
    }, []);

    useEffect(() => {
        if (reduxLocations && reduxLocations.length > 0) {
            setUseLocationDefaults(false);
        } else {
            setUseLocationDefaults(true);
        }

        if (locationFilterOpen.current) {
            return;
        }

        fetchTableData(false);
    }, [reduxLocations]);

    function onSelectedLocationsChange(locations) {
        dispatch({
            type: "SET_EMPLOYEE_REVIEWS_LOCATIONS",
            payload: locations
        });
    }

    function onLocationFilterOpened() {
        locationFilterOpen.current = true;
    }

    function onLocationFilterClosed() {
        locationFilterOpen.current = false;
        fetchTableData(false);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => !prevState);
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
        setPageNumber(1);
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
        setPageNumber(1);
    }

    function onTableSort(header, direction) {
        setSortedDir(direction);
        setSortedBy(header);
    }

    function mapFilters() {
        let mappedFilters = (currentAppliedFilters.current ?? []).map((f) => ({ key: f.name, value: f.value }));

        if (reduxLocations != null) {
            const mappedLocations = reduxLocations.map((locationId) => ({ key: "Location", value: locationId }));
            mappedFilters = [...mappedFilters, ...mappedLocations];
        }

        return mappedFilters;
    }

    // Returns whether the caller can open the review's wizard along with the reason it
    // cannot, so the same result drives both the link and the tooltip explaining its absence.
    function resolveCompletability(item) {
        if (!canPerformReviews) {
            return { canComplete: false, reason: noPerformReviewAccessMessage };
        }

        if (item.canComplete === false) {
            const permissionMessages = item.permissionMessages ?? [];

            if (permissionMessages.length > 0) {
                return { canComplete: false, reason: permissionMessages.join(" ") };
            }

            return { canComplete: false, reason: noReviewTopicAccessMessage };
        }

        return { canComplete: true, reason: "" };
    }

    function buildRows(items) {
        return items.map((item) => {
            const completability = resolveCompletability(item);

            let employeeName = (
                <Link to={`/employee-reviews/pending-reviews/${item.pendingReviewId}/complete`}>{item.employee.name}</Link>
            );

            if (!completability.canComplete) {
                // The tooltip renders no element of its own, so the name needs a wrapping
                // span to give it a hoverable target.
                employeeName = (
                    <Tooltip content={completability.reason}><span>{item.employee.name}</span></Tooltip>
                );
            }

            return [
                <div key={`pending-employee-${item.pendingReviewId}`} className="inline-items">
                    <EmployeeStatus
                        statusId={item.employee.status?.id}
                        typeId={item.employee.type?.id}
                        typeAttributeMappings={employeeTypeOptions}
                        statusAttributeMappings={employeeStatusOptions}
                    />
                    {employeeName}
                </div>,
                item.location,
                item.reviewType,
                <DateToLocal key={`pending-due-${item.employee.id}`} format="DD/MM/YYYY">{item.dueDate}</DateToLocal>,
                renderStatusBand(item.statusBand),
                item.raisedBy,
                item.topicCount
            ];
        });
    }

    function fetchTableData(forceRefresh) {
        if (initalSetupComplete) {
            if (
                currentTableSortDir.current === sortedDir &&
                currentPageNumber.current === pageNumber &&
                currentPageSize.current === pageSize &&
                currentTableSortBy.current === sortedBy &&
                currentAppliedFilters.current === appliedFilters &&
                currentSearchTerm.current === debouncedSearchTerm &&
                // Compare the location contents, not the count, so swapping one
                // location for another still triggers a refetch.
                currentLocations.current === reduxLocations.join(',') &&
                !forceRefresh
            ) {
                // If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
                return;
            }

            currentTableSortBy.current = sortedBy;
            currentPageNumber.current = pageNumber;
            currentPageSize.current = pageSize;
            currentTableSortDir.current = sortedDir;
            currentAppliedFilters.current = appliedFilters;
            currentSearchTerm.current = debouncedSearchTerm;
            currentLocations.current = reduxLocations.join(',');

            setIsLoading(true);

            const searchRequest = {
                requestCount: pageSize,
                pageNumber: pageNumber,
                searchTerm: debouncedSearchTerm,
                sortField: sortedBy.sortName,
                sortDirection: sortedDir,
                filters: mapFilters()
            };

            EmployeeReviewsService.searchPendingReviews(searchRequest)
                .then((r) => {
                    setTableRows(r?.data != null ? buildRows(r.data) : []);
                    setTotalRecords(r?.totalCount ?? 0);
                    setIsLoading(false);
                })
                .catch(() => {
                    NotificationToaster.show(Intent.DANGER, "Failed to get pending reviews. Please refresh the page.");
                    setIsLoading(false);
                });
        }
    }

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        pageSize,
        sortedBy,
        sortedDir,
        debouncedSearchTerm,
        initalSetupComplete,
        appliedFilters
    ]);

    return (
        <>
            <div className="filter-controls spacer-bottom">
                <div className="inline-items">
                    <div className="inline-item fill">
                        <div className="form-field-full">
                            <FormTextInput
                                placeholder="Search employees"
                                onChange={onSearchChange}
                                value={searchTerm}
                                large
                                disabled={isLoading}
                                icon="search"
                                id="pending-reviews-search-field"
                            />
                        </div>
                    </div>
                    <div className="inline-item">
                        <FormLocationSelector
                            businessArea={"Tacho"}
                            loading={!initalSetupComplete}
                            selectedLocations={reduxLocations}
                            setSelectedLocations={onSelectedLocationsChange}
                            onLocationListOpened={onLocationFilterOpened}
                            onLocationListClose={onLocationFilterClosed}
                            useLocationDefaults={useLocationDefaults}
                            useHierarchy={useHierarchy}
                            setUseHierarchy={setUseHierarchy}
                            locationDefaultsToTrue={isLoading ? null : true}
                            disabled={isLoading}
                        />
                    </div>
                    <div className="inline-item">
                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                    </div>
                </div>
                <Filter
                    filterName={filterName}
                    filters={pageFilters}
                    visible={showFilters}
                    numOfColumns={3}
                    onUpdate={onFilterChange}
                    onClickOutside={onToggleFilter}
                />
            </div>

            <ListingTable
                id="pending-reviews-listing"
                pageSize={pageSize}
                headers={tableHeaders}
                data={tableRows}
                loadingData={isLoading}
                onPagingChange={onPagingChange}
                totalRecordCount={totalRecords}
                noDataMessage={noDataMessage}
                sortable
                sortedBy={sortedBy}
                sortedDir={sortedDir}
                sortableHeaders={sortableHeaders}
                onSort={onTableSort}
            />
        </>
    );
}
